import { ref, inject, onBeforeUnmount } from 'vue';
import { useToast } from 'vue-toastification';
import { validateFileType } from '@/helpers/field-validation';
import { useUploadUserCvMutation } from '@/api/queries/user/useUploadUserCv';
import useMeQuery from '@/api/queries/me/useMeQuery';
import { useCvStore } from './cvUploadStore';
import { MixpanelInstance } from '@/plugins/mixpanel/mixpanel';
import type { Translate } from '@/plugins/i18n/types';
import { useMediaStore } from '../media/mediaStore';
import { useAvatarStore } from '@/features/avatar/avatarStore';

// Constants
const FILE_LIMITS = {
  maxBytes: 2 * 1024 * 1024, // 2 Mo
  acceptedTypes: ['application/pdf', 'text/plain'] as const,
};

// ==============================================
// Main composable
// ==============================================

export function useCvUpload() {
  const { auth } = useMeQuery();
  const toast = useToast();
  const t = inject('t') as Translate;
  const cv = useCvStore();
  const avatarStore = useAvatarStore();

  const userUuid = auth.value?.uuid as string | undefined;
  const isFirstConnection = !!auth.value?.user_data?.tutorial?.candidate_first_connection;
  // mutations
  const { mutateAsync: uploadCv } = useUploadUserCvMutation();
  // State
  const selectedFile = ref<File | null>(null);
  const isUploading = ref(false);
  type UploadError = 'type' | 'size';
  const error = ref<UploadError | null>(null);
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  // File handling
  function setFile(file: File | null) {
    if (!file) {
      selectedFile.value = null;
      error.value = null;
      return;
    }

    const isTypeValid = validateFileType(file, FILE_LIMITS.acceptedTypes);
    if (!isTypeValid) {
      selectedFile.value = null;
      error.value = 'type';
      return;
    }
    const isSizeValid = file.size <= FILE_LIMITS.maxBytes;
    if (!isSizeValid) {
      selectedFile.value = null;
      error.value = 'size';
      return;
    }

    selectedFile.value = file;
    error.value = null;
  }

  function clearFile() {
    selectedFile.value = null;
    error.value = null;
  }

  async function submit() {
    if (!selectedFile.value || isUploading.value || !userUuid) return false;

    try {
      isUploading.value = true;

      await uploadCv({ user_uuid: userUuid, file: selectedFile.value });

      // Track event
      MixpanelInstance.track('upload_cv', {
        email: auth.value?.email,
        user_uuid: auth.value?.uuid,
        requested_on: 'personal_account',
      });

      toast.success(t('general.save_success'));
      clearFile();
      cv.closeModal();
      if (isFirstConnection && !avatarStore.hasAvatar) useMediaStore().openModal();

      return true;
    } catch (e) {
      console.error('CV upload failed:', e);
      toast.error(t('general.error.general'));
      clearFile();
      return false;
    } finally {
      isUploading.value = false;
    }
  }

  onBeforeUnmount(() => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  });

  return { selectedFile, isUploading, error, setFile, clearFile, submit };
}
